import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../../firebase";

async function getFundBalanceMappings() {
  const snap = await getDoc(doc(db, "fieldMappings", "rowMappings"));
  if (!snap.exists()) throw new Error("Mapping document not found.");
  return snap.data();
}

function extractYearsFromTable(dataTables) {
  const allYears = new Set();
  Object.values(dataTables).forEach((rows) => {
    rows?.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (/^\d{4}_\d{4}$/.test(key)) {
          allYears.add(key);
        }
      });
    });
  });
  return [...allYears];
}

export async function saveBudgetReportEdits({
  selectedYear,
  revenuesData,
  expendituresData,
  fundBalanceData,
}) {
  if (!selectedYear) throw new Error("No year selected for saving edits.");

  try {
    const docRef = doc(db, "townGovernment", "revenue_expenditure_projections");
    const existingSnap = await getDoc(docRef);
    const existingData = existingSnap.exists() ? existingSnap.data() : {};
    const mappings = await getFundBalanceMappings();

    const updates = {}; // collect all changes by year key

    // 🧮 Fund Balance
    const fundYears = extractYearsFromTable({
      operatingData: fundBalanceData.operatingData,
      fundsData: fundBalanceData.fundsData,
      ufbData: fundBalanceData.ufbData,
    });

    for (const yearKey of fundYears) {
      const current = existingData[yearKey] || {};
      const currentProj = current.projections || { years: {} };
      const currentProjYears = currentProj.years || {};

      const newProj = {
        ...currentProj,
        years: {
          ...currentProjYears,
          [yearKey]: {
            operatingResults: {},
            funds: {},
            ufbBenchmarks: {},
          },
        },
      };

      fundBalanceData.operatingData?.forEach((row) => {
        const val = row[yearKey];
        const key = mappings.operatingResults?.[row.label];
        if (key) {
          newProj.years[yearKey].operatingResults[key] = val;
          if (key === "actualGainLoss") newProj.years[yearKey].operatingResults.type = "actual";
          if (key === "netGainLoss") newProj.years[yearKey].operatingResults.type = "projected";
        }
      });

      fundBalanceData.fundsData?.forEach((row) => {
        const val = row[yearKey];
        const key = mappings.funds?.[row.label];
        if (key) newProj.years[yearKey].funds[key] = val;
      });

      fundBalanceData.ufbData?.forEach((row) => {
        const val = row[yearKey];
        const key = mappings.ufbBenchmarks?.[row.label];
        if (key) newProj.years[yearKey].ufbBenchmarks[key] = val;
      });

      updates[yearKey] = {
        ...(updates[yearKey] || existingData[yearKey] || {}),
        projections: newProj,
      };
    }

    // 📊 Revenues
    for (const [yearKey, data] of Object.entries(revenuesData)) {
      const current = existingData[yearKey] || {};
      updates[yearKey] = {
        ...(updates[yearKey] || current),
        Revenues_and_Transfers: data,
      };
    }

    // 💸 Expenditures
    for (const [yearKey, data] of Object.entries(expendituresData)) {
      const current = existingData[yearKey] || {};
      updates[yearKey] = {
        ...(updates[yearKey] || current),
        Expenditures_and_Transfers: data,
      };
    }

    // ✅ Write merged updates
    await setDoc(docRef, updates, { merge: true });

    return { success: true };
  } catch (error) {
    console.error("[💥 Firestore save error]", error);
    return { success: false, error };
  }
}
